import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { BehaviorSubject, Observable, of } from 'rxjs'
import { deserializeArray, selectOnce, toMoment } from '@tokeet-frontend/tv3-platform'
import * as fromRoot from '../state'
import { Store } from '@ngrx/store'
import { CalendarResponse } from '@tv3/interfaces/responses/calendar.response'
import {
  CalendarEvent,
  CreateHoldEventpayload,
  FetchCalendarEventsPayload,
  LoadHoldEventsParams,
  UpdateHoldEventPayload,
} from '@tv3/store/calendar/calendar.model'
import { selectIsDoubleBookingDisabled } from '@tv3/store/preferences/preferences.selectors'
import { concatMap, map, switchMap, toArray } from 'rxjs/operators'
import * as R from 'ramda'
import * as moment from 'moment'
import { CalendarEventView } from './calendar.view'
import { isRangeOverlapping } from './calendar.helpers'

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  constructor(private http: HttpClient, private store: Store<fromRoot.State>) {}

  getAll(payload: FetchCalendarEventsPayload = {}) {
    const url = '@api/calendar/all'

    // If start is provided and is more than 360 days in the past, set past to start value
    if (payload.start) {
      const threeSixtyDaysInSeconds = 360 * 24 * 60 * 60 // 31104000 seconds
      const currentTime = Math.floor(Date.now() / 1000)
      const startTime = payload.start

      if (currentTime - startTime > threeSixtyDaysInSeconds) {
        payload.past = startTime
      }
    }

    return this.http.post<CalendarResponse[]>(url, payload).pipe(deserializeArray<CalendarEvent>(CalendarEvent))
  }

  allHolds(params?: LoadHoldEventsParams, loadAll = false): Observable<CalendarEvent[]> {
    params = params || {}
    const url = `@api/calendar/holds/all/${params.rentalId ? `rental/${params.rentalId}` : ''}`
    params.limit = params.limit || 1000
    params.skip = params.skip || 0

    return this.http.get<object[]>(url, { params }).pipe(
      deserializeArray<CalendarEvent>(CalendarEvent),
      switchMap((items) =>
        items.length < params.limit || !loadAll
          ? of(items)
          : this.allHolds({ ...params, skip: params.skip + params.limit }, true).pipe(
              map((items2) => [...items, ...items2])
            )
      )
    )
  }

  create(requests: CreateHoldEventpayload[]): Observable<CalendarEvent[]> {
    const url = '@api/calendar/'

    return of(...requests).pipe(
      concatMap((request) =>
        this.http
          .post<object>(url, { ...request, source: request.source || 'tokeet' })
          .pipe(map((hold) => CalendarEvent.deserialize(hold)))
      ),
      toArray()
    )
  }

  createRecurring(requests: CreateHoldEventpayload[]) {
    const url = '@api/calendars'
    return this.http.post<any[]>(url, requests).pipe(deserializeArray<CalendarEvent>(CalendarEvent))
  }

  updateRecurring(groupId: string, payload: UpdateHoldEventPayload) {
    const url = `@api/calendars/update/${groupId}`
    return this.http.put<any[]>(url, payload).pipe(deserializeArray<CalendarEvent>(CalendarEvent))
  }

  deleteRecurring(groupId: string) {
    const url = `@api/calendars/delete/${groupId}`
    return this.http.delete(url).pipe(deserializeArray<CalendarEvent>(CalendarEvent))
  }

  update(holdId: string, payload: UpdateHoldEventPayload): Observable<CalendarEvent> {
    const url = `@api/calendar/update/${holdId}`

    return this.http.put(url, payload).pipe(map((hold) => CalendarEvent.deserialize(hold)))
  }

  delete(id: string) {
    const url = `@api/calendar/delete/${id}`

    return this.http.delete(url)
  }

  deleteBatch(ids: string[]): Observable<string[]> {
    const url = '@api/calendar/delete/batch'

    return this.http.put<string[]>(url, { ids })
  }

  get(id: string) {
    const url = `@api/calendar/${id}`
    return this.http.get(url).pipe(map((hold) => CalendarEvent.deserialize(hold)))
  }

  checkOverlapping(range: { start; end }, events: CalendarEvent[], isBooking = true) {
    const result = new BehaviorSubject<CalendarEvent>(null)

    this.store
      .pipe(
        selectOnce(selectIsDoubleBookingDisabled),
        map((doubleBookingDisabled) => {
          const eventStart = R.is(Number, range.start) ? range.start : moment.utc(range.start).unix()
          const eventEnd = R.is(Number, range.end) ? range.end : moment.utc(range.end).unix()

          let overlappingEvent: CalendarEvent = null

          for (let i = 0, len = events.length; i < len; i++) {
            const event = events[i]
            if (isBooking && !!event.bookingId && !doubleBookingDisabled) {
              // two bookings can overlap
              continue
            }
            const start = R.is(Number, event.start) ? event.start : moment.utc(event.start).unix()
            const end = R.is(Number, event.start) ? event.end : moment.utc(event.end).unix()

            if (isRangeOverlapping({ start: eventStart, end: eventEnd }, { start, end })) {
              overlappingEvent = event
              break
            }
          }
          return overlappingEvent
        })
      )
      .subscribe((overlappingEvent) => result.next(overlappingEvent))

    return result.value
  }

  getEventID(event: CalendarEvent) {
    return event.id || event.bookingId
  }

  getEventTimeRange(event: CalendarEventView) {
    if (!event.isHoldEvent) return { start: event.start, end: event.end, event }

    const start = toMoment(event.start).hour(10).minute(0).second(0)
    const end = toMoment(event.end).hour(10).minute(0).second(0)

    // TV3-1124:
    // if a hold event is a single day. the "start" and "date" fields will have the same value.
    // in this case, we add some hours to "end" field to make sure "start" and "end" time are different.
    // so the event will be visible in the calendar.

    if (start.isSame(end, 'date')) {
      end.endOf('date')
    }

    if (start.isSame(end, 'hour')) {
      end.add(6, 'hour')
    }

    return { start: start.unix(), end: end.unix(), event }
  }
}
